import { assumptionDefinitionMarkdown } from "@hightouch/lib/llm/agentic-settings-entities/definition-markdown";
import type { InlineCitationRefs } from "@hightouch/lib/llm/inline-citation-tags";
import type { ResponseMessage } from "@hightouch/lib/llm/schema";
import { AgentDefinitionStatus } from "src/graphql";
import { isJourneyGraphEnvelopeContent } from "src/pages/journeys/utils/journey-output-adapter";

export type InteractionResponseMessage = ResponseMessage & {
  interactionId?: string;
};

type ResponseMessageOfType<T extends ResponseMessage["type"]> = Extract<
  ResponseMessage,
  { type: T }
>;

/** What a turn cited: an assumption it settled on, or a saved definition it relied on. */
export type CitedDefinitionEntry = {
  kind: "assumption" | "definition";
  // Null for Harness V2 citations, which don't need it to save an assumption from a private session.
  interactionId: string | null;
  // An assumption's key, which inline tags cite it by.
  key: string | null;
  title: string;
  // The body without its SQL, which identifies an entry that has no id.
  content: string;
  markdown: string;
  usageGuidance: string | null;
  // Null when the entry was never saved as a definition, or the agent omitted the id.
  definitionId: string | null;
  versionId: string | null;
  // The status the version was cited with, when known.
  status: AgentDefinitionStatus | null;
};

/** The entries one citations pill shows: a v1 interaction's, or a single Harness V2 reply's. */
export type CitationGroup = {
  // The v1 interaction, for analytics. Null for a Harness V2 reply.
  interactionId: string | null;
  // The transcript message that sent a Harness V2 reply, for analytics. Null for v1, and while the reply streams.
  messageId: string | null;
  /** Its new assumptions followed by the saved definitions it referenced, including entries no tag can cite. */
  entries: CitedDefinitionEntry[];
  byAssumptionKey: ReadonlyMap<string, CitedDefinitionEntry>;
  /** The saved definitions it relied on, which a v1 tag cites by definition id. */
  byDefinitionId: ReadonlyMap<string, CitedDefinitionEntry>;
  /** Every entry with a version, which a Harness V2 tag cites by version id. */
  byVersionId: ReadonlyMap<string, CitedDefinitionEntry>;
};

export type SessionOutputItem = {
  key: string;
  message: ResponseMessageOfType<"csv" | "pptx">;
};

export type SessionResourceType = "audience" | "journey" | "trait";

type SessionResourceMessageType = SessionResourceType | "journey_reference";

/** The resource the host page shows, so its Data row doesn't reopen it in a new tab. */
export type SessionResource = { type: SessionResourceType; id: string };

export type SessionResourceItem = {
  key: string;
  resourceType: SessionResourceType;
  /** The latest message carrying the resource. */
  message: ResponseMessageOfType<SessionResourceMessageType>;
  /** Null for a draft. */
  resourceId: string | null;
  name: string;
  status: "created" | "referenced" | "draft";
  messageIndex: number;
};

export type SessionContext = {
  citationGroupsById: Map<string, CitationGroup>;
  /** Every cited entry across the session, deduped, most recent first. */
  citedEntries: CitedDefinitionEntry[];
  outputs: SessionOutputItem[];
  resources: SessionResourceItem[];
};

type DeriveSessionContextArgs = {
  messages: readonly InteractionResponseMessage[];
  activeInteractionId: string | null;
};

export function deriveSessionContext(
  args: DeriveSessionContextArgs,
): SessionContext {
  const completedInteractionIds = getCompletedInteractionIds(args);
  // Each value array holds its interaction's messages oldest first; the keys are inserted newest interaction first.
  const assumptionsMessagesByInteractionId = new Map<
    string,
    ResponseMessageOfType<"assumptions">[]
  >();
  const outputRowsByKey = new Map<string, SessionOutputItem>();
  const resourceRowsByKey = new Map<string, SessionResourceItem>();

  // Walks newest first, so the first sighting of an item places it.
  for (
    let messageIndex = args.messages.length - 1;
    messageIndex >= 0;
    messageIndex--
  ) {
    const message = args.messages[messageIndex];
    if (
      !message?.interactionId ||
      !completedInteractionIds.has(message.interactionId)
    ) {
      continue;
    }

    switch (message.type) {
      case "assumptions": {
        const interactionMessages = assumptionsMessagesByInteractionId.get(
          message.interactionId,
        );
        if (interactionMessages) {
          interactionMessages.unshift(message);
        } else {
          assumptionsMessagesByInteractionId.set(message.interactionId, [
            message,
          ]);
        }
        break;
      }
      case "csv":
      case "pptx": {
        const outputRow = buildOutputItem(message, message.interactionId);
        if (!outputRowsByKey.has(outputRow.key)) {
          outputRowsByKey.set(outputRow.key, outputRow);
        }
        break;
      }
      case "audience":
      case "journey":
      case "journey_reference":
      case "trait": {
        const resourceRow = buildResourceItem(message, messageIndex);
        const newerResourceRow = resourceRowsByKey.get(resourceRow.key);
        if (!newerResourceRow) {
          resourceRowsByKey.set(resourceRow.key, resourceRow);
        } else if (resourceRow.status === "created") {
          resourceRowsByKey.set(resourceRow.key, {
            ...newerResourceRow,
            status: "created",
          });
        }
        break;
      }
    }
  }

  const citationGroupsById = new Map(
    [...assumptionsMessagesByInteractionId].map(
      ([interactionId, assumptionsMessages]) => [
        interactionId,
        citationGroupFromMessages(interactionId, assumptionsMessages),
      ],
    ),
  );
  return {
    ...citationsSessionContext(citationGroupsById),
    outputs: [...outputRowsByKey.values()],
    resources: [...resourceRowsByKey.values()],
  };
}

/** A session context of citations alone, from citation groups inserted newest first. */
export function citationsSessionContext(
  citationGroupsById: Map<string, CitationGroup>,
): SessionContext {
  return {
    citationGroupsById,
    citedEntries: dedupeCitedEntries(
      [...citationGroupsById.values()].flatMap(({ entries }) => entries),
    ),
    outputs: [],
    resources: [],
  };
}

export function getLastAssistantMessageIndexesByInteractionId(
  messages: readonly InteractionResponseMessage[],
): Map<string, number> {
  const indexes = new Map<string, number>();
  messages.forEach((message, index) => {
    if (message.type === "assistant" && message.interactionId) {
      indexes.set(message.interactionId, index);
    }
  });
  return indexes;
}

function getCompletedInteractionIds(
  args: DeriveSessionContextArgs,
): Set<string> {
  const completedInteractionIds = new Set<string>();
  const endedInteractionIds = new Set<string>();
  for (const message of args.messages) {
    if (!message.interactionId) continue;
    completedInteractionIds.add(message.interactionId);
    // An interaction with an error or canceled message is dropped entirely.
    // Its resource/file cards are presented in the final message, which is never persisted.
    if (message.type === "error" || message.type === "canceled") {
      endedInteractionIds.add(message.interactionId);
    }
  }
  for (const endedInteractionId of endedInteractionIds) {
    completedInteractionIds.delete(endedInteractionId);
  }
  if (args.activeInteractionId) {
    completedInteractionIds.delete(args.activeInteractionId);
  }
  return completedInteractionIds;
}

export type CitedVersion = {
  versionId: string;
  status: AgentDefinitionStatus;
};

// Null for a turn processed before versions were stamped onto messages, or a reference whose id wasn't served that turn.
export function citedEntryVersion({
  versionId,
  status,
}: CitedDefinitionEntry): CitedVersion | null {
  return versionId && status ? { versionId, status } : null;
}

// Definition titles aren't unique, so an id-less entry is identified by its title and body together.
function citedEntryContentKey({
  title,
  content,
}: CitedDefinitionEntry): string {
  return `content:${JSON.stringify([title, content])}`;
}

export function citedEntryKey(citedEntry: CitedDefinitionEntry): string {
  return citedEntry.definitionId ?? citedEntryContentKey(citedEntry);
}

/** An entry of a v1 assumptions message's `assumptions` array. */
export function assumptionCitedEntry(
  entry: ResponseMessageOfType<"assumptions">["content"]["assumptions"][number],
  interactionId: string | null,
): CitedDefinitionEntry {
  return {
    kind: "assumption",
    interactionId,
    key: entry.key ?? null,
    title: entry.assumption,
    content: entry.content,
    markdown: assumptionDefinitionMarkdown(entry),
    usageGuidance: entry.usageGuidance ?? null,
    definitionId: entry.definitionId ?? null,
    versionId: entry.versionId ?? null,
    status: AgentDefinitionStatus.Assumed,
  };
}

function referencedCitedEntry(
  entry: ResponseMessageOfType<"assumptions">["content"]["referencedMemories"][number],
  interactionId: string,
): CitedDefinitionEntry {
  return {
    kind: "definition",
    interactionId,
    key: null,
    title: entry.title,
    content: entry.content,
    markdown: entry.content,
    usageGuidance: null,
    definitionId: entry.definitionId ?? null,
    versionId: entry.versionId ?? null,
    status: entry.status ?? null,
  };
}

/** Indexes a turn's entries for its inline tags, keeping the first entry for each key or id. */
export function buildCitationGroup(
  citedEntries: readonly CitedDefinitionEntry[],
  {
    interactionId,
    messageId,
  }: Pick<CitationGroup, "interactionId" | "messageId">,
): CitationGroup {
  const byAssumptionKey = new Map<string, CitedDefinitionEntry>();
  const byDefinitionId = new Map<string, CitedDefinitionEntry>();
  const byVersionId = new Map<string, CitedDefinitionEntry>();
  for (const citedEntry of citedEntries) {
    const { kind, key, definitionId, versionId } = citedEntry;
    if (kind === "assumption" && key && !byAssumptionKey.has(key)) {
      byAssumptionKey.set(key, citedEntry);
    }
    if (
      kind === "definition" &&
      definitionId &&
      !byDefinitionId.has(definitionId)
    ) {
      byDefinitionId.set(definitionId, citedEntry);
    }
    if (versionId && !byVersionId.has(versionId)) {
      byVersionId.set(versionId, citedEntry);
    }
  }
  return {
    interactionId,
    messageId,
    entries: dedupeCitedEntries([...citedEntries]),
    byAssumptionKey,
    byDefinitionId,
    byVersionId,
  };
}

// Validation rejects a repeated key or id within one assumptions message, so the first entry wins only across messages.
function citationGroupFromMessages(
  interactionId: string,
  assumptionsMessages: ResponseMessageOfType<"assumptions">[],
): CitationGroup {
  return buildCitationGroup(
    [
      ...assumptionsMessages.flatMap(({ content }) =>
        content.assumptions.map((entry) =>
          assumptionCitedEntry(entry, interactionId),
        ),
      ),
      ...assumptionsMessages.flatMap(({ content }) =>
        (content.referencedMemories ?? []).map((entry) =>
          referencedCitedEntry(entry, interactionId),
        ),
      ),
    ],
    { interactionId, messageId: null },
  );
}

/** The entries one inline tag cites, in tag order, deduped, with references its interaction doesn't resolve dropped. */
export function resolveParagraphCitedEntries(
  citations: CitationGroup | undefined,
  { assumptionKeys, referencedIds, versionIds }: InlineCitationRefs,
): CitedDefinitionEntry[] {
  if (!citations) return [];
  return dedupeCitedEntries([
    ...assumptionKeys.flatMap(
      (key) => citations.byAssumptionKey.get(key) ?? [],
    ),
    ...referencedIds.flatMap((id) => citations.byDefinitionId.get(id) ?? []),
    ...versionIds.flatMap((id) => citations.byVersionId.get(id) ?? []),
  ]);
}

// Entries with an id dedupe by it. An id-less entry folds into the entry with
// the same title and body that has an id, or else dedupes by title and body.
function dedupeCitedEntries(
  citedEntries: CitedDefinitionEntry[],
): CitedDefinitionEntry[] {
  const definitionIdsByContentKey = new Map<string, string>();
  for (const citedEntry of citedEntries) {
    const definitionId = citedEntry.definitionId;
    const contentKey = citedEntryContentKey(citedEntry);
    if (definitionId && !definitionIdsByContentKey.has(contentKey)) {
      definitionIdsByContentKey.set(contentKey, definitionId);
    }
  }

  const citedEntriesByKey = new Map<string, CitedDefinitionEntry>();
  for (const citedEntry of citedEntries) {
    const definitionId = citedEntry.definitionId;
    const contentKey = citedEntryContentKey(citedEntry);
    const entryKey =
      definitionId ?? definitionIdsByContentKey.get(contentKey) ?? contentKey;
    const placedEntry = citedEntriesByKey.get(entryKey);
    if (!placedEntry || (!placedEntry.definitionId && definitionId)) {
      citedEntriesByKey.set(entryKey, citedEntry);
    }
  }
  return [...citedEntriesByKey.values()];
}

function buildOutputItem(
  message: ResponseMessageOfType<"csv" | "pptx">,
  interactionId: string,
): SessionOutputItem {
  const { content } = message;
  return {
    key: `${message.type}:${content.fileReference ?? `${interactionId}:${content.filename}`}`,
    message,
  };
}

function buildResourceItem(
  message: ResponseMessageOfType<SessionResourceMessageType>,
  messageIndex: number,
): SessionResourceItem {
  const { createdResourceId, referencedResourceId, name } =
    extractResourceIdsAndName(message);
  const resourceId = createdResourceId ?? referencedResourceId;
  const resourceType =
    message.type === "journey_reference" ? "journey" : message.type;
  return {
    // Drafts key by name, so an agent revising one unsaved resource only results in a single entry.
    key: resourceId
      ? `${resourceType}:${resourceId}`
      : `${resourceType}:draft:${name}`,
    resourceType,
    message,
    resourceId,
    name,
    status: createdResourceId
      ? "created"
      : referencedResourceId
        ? "referenced"
        : "draft",
    messageIndex,
  };
}

function extractResourceIdsAndName(
  message: ResponseMessageOfType<SessionResourceMessageType>,
): {
  createdResourceId: string | null;
  referencedResourceId: string | null;
  name: string;
} {
  switch (message.type) {
    case "audience":
    case "trait":
      return {
        createdResourceId: normalizeResourceId(message.content.newId),
        referencedResourceId: normalizeResourceId(message.content.existingId),
        name: message.content.name,
      };
    case "journey": {
      const { content } = message;
      if (isJourneyGraphEnvelopeContent(content)) {
        return {
          createdResourceId: normalizeResourceId(content.newId),
          referencedResourceId: normalizeResourceId(content.journey.id),
          name: content.journey.name,
        };
      }
      return {
        createdResourceId: normalizeResourceId(content.newId),
        referencedResourceId: null,
        name: content.name,
      };
    }
    case "journey_reference":
      return {
        createdResourceId: null,
        referencedResourceId: normalizeResourceId(message.content.existingId),
        name: message.content.name,
      };
  }
}

function normalizeResourceId(
  resourceId: string | number | null | undefined,
): string | null {
  return resourceId == null || resourceId === "" ? null : String(resourceId);
}
